import { Component } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AdminService } from 'src/app/services/admin/admin.service';

@Component({
  selector: 'app-x-voc-list',
  templateUrl: './x-voc-list.component.html',
  styleUrl: './x-voc-list.component.scss'
})
export class XVocListComponent {

  // Store selected course type from URL
  courseType: string = '';

  // Store all UOC courses
  allUOCCourses: any[] = [];

  // Store all VOC courses
  allVOCCourses: any[] = [];

  // Store displayed course list
  courses: any[] = [];

  // Track loading state
  isLoading: boolean = false;


  // ============================================================
  // ALL COURSES PAGE SECTIONS
  // ============================================================

  // Store active all courses section
  activeAllCoursesSection: 'POPULAR' | 'OTHER' = 'POPULAR';

  // Store other course options
  otherCourseOptions: string[] = ["001-TEST-COURSE"];

  // Store selected other course
  selectedOtherCourse = '';

  // Store custom other course
  customOtherCourse = '';

  // Store enquiry name
  otherEnquiryName = '';

  // Store enquiry email
  otherEnquiryEmail = '';

  // Store enquiry phone
  otherEnquiryPhone = '';

  // Store enquiry message
  otherEnquiryMessage = '';

  // Track other enquiry submission
  isSendingOtherEnquiry = false;

  // Store other enquiry error message
  otherEnquiryErrorMessage = '';

  // Store other enquiry success message
  otherEnquirySuccessMessage = '';

  // Store current page
  currentPage: number = 1;

  // Store page size
  pageSize: number = 8;

  // Control enquiry success popup visibility
  showEnquirySuccessPopup: boolean = false;

  //store search keyword
  searchKeyword: string = "";

  //store selected course filter
  selectedCourseFilter: string = '';

  //store short course options list
  shortCourseOptions: any[] = [];

  //store main component
  isMainComponent: boolean = false;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private adminService: AdminService
  ) {
  }


  // Initialise course list
  ngOnInit(): void {


    // Read route query parameters
    this.route.queryParamMap.subscribe(params => {

      // Check if the URL path is /course-short
      this.isMainComponent = this.router.url.split('?')[0] === '/course-short';

      // Store course type from URL
      this.courseType = (params.get('type') ?? 'ALL').toUpperCase();

      // Reset current page
      this.currentPage = 1;

      // Set default all courses section
      this.activeAllCoursesSection = 'POPULAR';

      // Load VOC courses
      this.loadVOCCourses();

    });

  }



  // Load VOC courses
  loadVOCCourses(): void {

    // Start loading
    this.isLoading = true;

    this.adminService
      .getXVOCList('GET_ALL')
      .subscribe({

        next: (response: any[]) => {

          // Store VOC courses
          this.allVOCCourses =
            response || [];

          // Store displayed courses
          this.courses =
            this.allVOCCourses;

          // Stop loading
          this.isLoading = false;
        },

        error: (error) => {

          // Log VOC error
          console.error(
            'VOC Error:',
            error
          );

          // Reset displayed courses
          this.courses = [];

          // Stop loading
          this.isLoading = false;
        }

      });

  }
  // Get course code
  getCourseCode(course: any): string {

    // Get course type
    const type =
      this.courseType === 'ALL'
        ? course?.courseType
        : this.courseType;

    // Return VOC code
    if (type === 'VOC') {
      return 'VOC - ' + course?.vocCode || 'VOC';
    }

    // Return UOC code
    return course?.courseCode || 'UOC';
  }


  // Get course type only
  getCourseCodeOnly(course: any): string {

    // Get course type
    const type =
      this.courseType === 'ALL'
        ? course?.courseType
        : this.courseType;

    // Return course type
    return type;
  }


  // ============================================================
  // COURSE TITLE
  // ============================================================

  // Get course title
  getCourseTitle(course: any): string {

    
      return course?.vocName || 'VOC';
   
  }


  getCourseDescription(course: any): string {

    if (
      !course?.description ||
      course.description.trim().length === 0
    ) {
      return '-';
    }

    const maxLength = 80;

    const container =
      document.createElement('div');

    container.innerHTML =
      course.description;

    let currentLength = 0;
    let isTruncated = false;


    const truncateNode = (
      node: Node
    ): Node | null => {

      // Text node
      if (
        node.nodeType === Node.TEXT_NODE
      ) {

        const text =
          node.textContent || '';

        const remaining =
          maxLength - currentLength;

        if (remaining <= 0) {
          isTruncated = true;
          return null;
        }

        if (text.length > remaining) {

          currentLength += remaining;
          isTruncated = true;

          return document.createTextNode(
            text.substring(0, remaining)
          );
        }

        currentLength +=
          text.length;

        return document.createTextNode(
          text
        );
      }


      // HTML element
      if (
        node.nodeType === Node.ELEMENT_NODE
      ) {

        const clonedElement =
          (node as HTMLElement)
            .cloneNode(false) as HTMLElement;

        for (
          const child of Array.from(node.childNodes)
        ) {

          if (currentLength >= maxLength) {
            isTruncated = true;
            break;
          }

          const clonedChild =
            truncateNode(child);

          if (clonedChild) {
            clonedElement.appendChild(
              clonedChild
            );
          }
        }

        return clonedElement;
      }


      return null;
    };


    const result =
      document.createElement('div');


    for (
      const child of Array.from(container.childNodes)
    ) {

      if (currentLength >= maxLength) {
        isTruncated = true;
        break;
      }

      const clonedChild =
        truncateNode(child);

      if (clonedChild) {
        result.appendChild(
          clonedChild
        );
      }
    }


    // Add ... inside the last text node
    if (isTruncated) {

      const addEllipsis = (
        node: Node
      ): boolean => {

        const children =
          Array.from(node.childNodes)
            .reverse();

        for (
          const child of children
        ) {

          if (
            child.nodeType === Node.TEXT_NODE &&
            child.textContent?.trim()
          ) {

            // Remove existing trailing dots first
            const text =
              child.textContent
                .replace(/[\s.…]+$/, '');

            child.textContent =
              text + '...';

            return true;
          }

          if (
            child.nodeType === Node.ELEMENT_NODE
          ) {

            if (addEllipsis(child)) {
              return true;
            }
          }
        }

        return false;
      };


      addEllipsis(result);
    }


    return result.innerHTML;
  }
  // ============================================================
  // COURSE IMAGE
  // ============================================================

  // Check course image
  hasCourseImage(course: any): boolean {

    return !!(
      course?.imageUrl &&
      course.imageUrl.trim().length > 0 &&
      !course.imageLoadFailed
    );
  }


  // Handle broken image
  onImageError(course: any): void {

    // Mark image load failure
    course.imageLoadFailed = true;
  }

  // Check course price availability
  hasPrice(course: any): boolean {

    return (
      (this.courseType === 'UOC' ||
        this.courseType === 'OSC' ||
        this.courseType === 'HRC') &&
      Number(course?.totalAmount ?? 0) > 0
    );
  }


  // Get course price
  getPrice(course: any): string {

    // Check price availability
    if (!this.hasPrice(course)) {

      return 'Enquire for pricing';
    }

    // Store currency code
    const currency =
      course?.currencyCode || 'AUD';

    // Format course price
    return new Intl.NumberFormat(
      'en-AU',
      {
        style: 'currency',
        currency: currency
      }
    ).format(
      Number(course.totalAmount)
    );
  }


  // Open course details
  openCourseDetails(course: any): void {


    // Read route query parameters
    this.route.queryParams.subscribe(params => {

      // Get current route with query parameters
      const currentUrl = this.router.url;

      // Store current URL in local storage
      localStorage.setItem(
        'PREVIOUS_COURSE_URL',
        currentUrl
      );

    });

    // Check course ID
    if (!course?.id) {
      return;
    }

    // Get course type
    const type = this.getCourseCodeOnly(course);

    // Navigate to course details
    this.router.navigate(
      ['/short-details'],
      {
        queryParams: {

          type: 'VOC',

          id: course.id
        }
      }
    );
  }


  // Get paginated courses
  get paginatedCourses(): any[] {

    // Calculate start index
    const startIndex =
      (this.currentPage - 1) *
      this.pageSize;

    // Calculate end index
    const endIndex =
      startIndex +
      this.pageSize;

    // Return paginated course list
    return this.courses.slice(
      startIndex,
      endIndex
    );
  }

  // Get total page count
  get totalPages(): number {

    // Check course list
    if (!this.courses.length) {
      return 0;
    }

    // Calculate total pages
    return Math.ceil(
      this.courses.length /
      this.pageSize
    );
  }


  // Get page numbers
  get pages(): number[] {

    return Array.from(
      {
        length: this.totalPages
      },
      (_, index) => index + 1
    );
  }


  // Go to selected page
  goToPage(page: number): void {

    // Validate page number
    if (
      page < 1 ||
      page > this.totalPages
    ) {
      return;
    }

    // Set current page
    this.currentPage = page;

    // Get course list section
    const section =
      document.getElementById(
        'short-course-list'
      );

    // Scroll to course list
    if (section) {

      section.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    }
  }


  // Go to previous page
  previousPage(): void {

    this.goToPage(
      this.currentPage - 1
    );
  }


  // Go to next page
  nextPage(): void {

    this.goToPage(
      this.currentPage + 1
    );
  }


}
